import { Post, Comment } from '../App';
import { useState, useEffect } from 'react';
import { apiConfig } from '../config/api';
import AnimatedContent from './AnimatedContent';
import PostMenu from './PostMenu';
import TimeTooltip from './TimeTooltip';

interface PostListProps {
  posts: Post[];
  userInfo: any;
  anonymousProfile?: any;
  onPostUpdated?: () => void;
}

export default function PostList({ posts, userInfo, anonymousProfile, onPostUpdated }: PostListProps) {
  const [expandedComments, setExpandedComments] = useState<Record<number, boolean>>({});
  const [comments, setComments] = useState<Record<number, Comment[]>>({});
  const [newComment, setNewComment] = useState<Record<number, string>>({});
  const [newReply, setNewReply] = useState<Record<number, string>>({});
  const [replyingTo, setReplyingTo] = useState<Record<number, number | null>>({});
  const [loadingComments, setLoadingComments] = useState<Record<number, boolean>>({});
  const [submittingComment, setSubmittingComment] = useState<Record<number, boolean>>({});
  const [likedPosts, setLikedPosts] = useState<Record<number, boolean>>({});
  const [likesCount, setLikesCount] = useState<Record<number, number>>({});
  const [commentCounts, setCommentCounts] = useState<Record<number, number>>({});
  const [showModal, setShowModal] = useState<boolean>(false);
  const [selectedPost, setSelectedPost] = useState<Post | null>(null);
  const [shareUrl, setShareUrl] = useState<string>('');
  const [revealedImages, setRevealedImages] = useState<Record<number, boolean>>({});
  const [showEditModal, setShowEditModal] = useState<boolean>(false);
  const [editingPost, setEditingPost] = useState<Post | null>(null);
  const [editText, setEditText] = useState<string>('');
  const [editImage, setEditImage] = useState<File | null>(null);
  const [editPreviewUrl, setEditPreviewUrl] = useState<string>('');
  const [isEditing, setIsEditing] = useState<boolean>(false);
  const [editingComment, setEditingComment] = useState<{postId: number, commentId: number, content: string} | null>(null);
  const [editCommentText, setEditCommentText] = useState<string>('');
  
  // 사용자 식별을 위한 함수 (IP 기반)
  const getCurrentUserId = () => {
    // App.tsx의 getEffectiveUserInfo와 동일한 로직 사용
    if (userInfo?.encrypted_yw_id) {
      return userInfo.encrypted_yw_id;
    }
    
    // localStorage에서 사용자 ID 가져오기
    return localStorage.getItem('user_id');
  };

  // 게시물이 현재 사용자의 것인지 확인 (게시글 작성자만 수정/삭제 가능)
  const isMyPost = (post: Post) => {
    const currentUserId = getCurrentUserId();
    if (!currentUserId) return false;
    
    // 게시글 작성자인지 확인
    return post.user_id === currentUserId;
  };

  // 댓글이 현재 사용자의 것인지 확인
  const isMyComment = (comment: Comment) => {
    const currentUserId = getCurrentUserId();
    return currentUserId && comment.user_id === currentUserId;
  };

  // 게시물 로드 시 댓글 수 로드
  useEffect(() => {
    posts.forEach(post => {
      if (!commentCounts[post.id]) {
        loadCommentCount(post.id);
      }
    });
  }, [posts]);

  // 컴포넌트 언마운트 시 스크롤 복원
  useEffect(() => {
    return () => {
      document.body.style.overflow = 'unset';
    };
  }, []);
  
  // 게시물 수정 및 삭제 핸들러
  const handleEditPost = (post: Post) => {
    setEditingPost(post);
    setEditText(post.diary_text);
    setEditImage(null);
    setEditPreviewUrl('');
    setShowEditModal(true);
  };

  const handleEditImageSelect = (event: React.ChangeEvent<HTMLInputElement>) => {
    const file = event.target.files?.[0];
    if (file) {
      setEditImage(file);
      const url = URL.createObjectURL(file);
      setEditPreviewUrl(url);
    }
  };

  const handleEditSubmit = async () => {
    if (!editingPost || !editText.trim()) {
      alert('내용을 입력해주세요!');
      return;
    }

    setIsEditing(true);

    try {
      let imagePath = editingPost.image_url?.split('/').pop() || '';

      // 새 이미지가 선택된 경우 업로드
      if (editImage) {
        // 1. 업로드 URL 요청
        const uploadUrlResponse = await fetch(apiConfig.endpoints.uploadUrl, {
          method: 'POST',
          headers: { 'Content-Type': 'application/json' },
          body: JSON.stringify({ filename: editImage.name })
        });

        if (!uploadUrlResponse.ok) {
          throw new Error('업로드 URL 생성 실패');
        }

        const { upload_url, file_path } = await uploadUrlResponse.json();

        // 2. 파일 업로드
        const formData = new FormData();
        formData.append('file', editImage);

        const uploadResponse = await fetch(upload_url, {
          method: 'POST',
          body: formData,
        });

        if (!uploadResponse.ok) {
          throw new Error('파일 업로드 실패');
        }

        imagePath = file_path;
      }

      // 3. 게시물 수정
      const headers: HeadersInit = {
        'Content-Type': 'application/json'
      };
      
      // 사용자 ID 헤더 추가
      const currentUserId = getCurrentUserId();
      if (currentUserId) {
        headers['X-Encrypted-Yw-ID'] = currentUserId;
        headers['X-Is-Login'] = '1';
      }
      
      const response = await fetch(apiConfig.endpoints.post(editingPost.id), {
        method: 'PUT',
        headers,
        body: JSON.stringify({
          diary_text: editText.trim(),
          image_path: imagePath
        })
      });

      if (!response.ok) {
        const errorData = await response.json();
        throw new Error(errorData.error || '게시물 수정 실패');
      }

      alert('게시물이 수정되었습니다!');
      setShowEditModal(false);
      onPostUpdated?.();

    } catch (error) {
      console.error('게시물 수정 오류:', error);
      alert(error instanceof Error ? error.message : '게시물 수정에 실패했습니다.');
    } finally {
      setIsEditing(false);
    }
  };

  const handleEditComment = (postId: number, commentId: number, content: string) => {
    setEditingComment({ postId, commentId, content });
    setEditCommentText(content);
  };

  const handleEditCommentSubmit = async () => {
    if (!editingComment || !editCommentText.trim()) {
      alert('댓글 내용을 입력해주세요!');
      return;
    }

    try {
      const headers: HeadersInit = {
        'Content-Type': 'application/json'
      };
      
      // 사용자 ID 헤더 추가
      const currentUserId = getCurrentUserId();
      if (currentUserId) {
        headers['X-Encrypted-Yw-ID'] = currentUserId;
        headers['X-Is-Login'] = '1';
      }
      
      const response = await fetch(apiConfig.endpoints.comment(editingComment.commentId), {
        method: 'PUT',
        headers,
        body: JSON.stringify({
          content: editCommentText.trim()
        })
      });

      if (!response.ok) {
        const errorData = await response.json();
        throw new Error(errorData.error || '댓글 수정 실패');
      }

      // 댓글 목록 새로고침
      loadComments(editingComment.postId);
      setEditingComment(null);
      setEditCommentText('');

    } catch (error) {
      console.error('댓글 수정 오류:', error);
      alert(error instanceof Error ? error.message : '댓글 수정에 실패했습니다.');
    }
  };

  const handleDeleteComment = async (commentId: number, postId: number) => {
    if (confirm('댓글을 삭제하시겠습니까?')) {
      try {
        const headers: HeadersInit = {
          'Content-Type': 'application/json'
        };
        
        // 사용자 ID 헤더 추가
        const currentUserId = getCurrentUserId();
        if (currentUserId) {
          headers['X-Encrypted-Yw-ID'] = currentUserId;
          headers['X-Is-Login'] = '1';
        }
        
        const response = await fetch(apiConfig.endpoints.comment(commentId), {
          method: 'DELETE',
          headers
        });

        if (!response.ok) {
          const errorData = await response.json();
          throw new Error(errorData.error || '댓글 삭제 실패');
        }

        // 댓글 목록 새로고침
        loadComments(postId);
        loadCommentCount(postId);

      } catch (error) {
        console.error('댓글 삭제 오류:', error);
        alert(error instanceof Error ? error.message : '댓글 삭제에 실패했습니다.');
      }
    }
  };

  const handleDeletePost = async (postId: number) => {
    if (confirm('게시물을 삭제하시겠습니까?')) {
      try {
        const headers: HeadersInit = {
          'Content-Type': 'application/json'
        };
        
        // 사용자 ID 헤더 추가
        const currentUserId = getCurrentUserId();
        if (currentUserId) {
          headers['X-Encrypted-Yw-ID'] = currentUserId;
          headers['X-Is-Login'] = '1';
        }
        
        const response = await fetch(apiConfig.endpoints.post(postId), {
          method: 'DELETE',
          headers
        });
        
        // 응답 상태 체크
        if (!response.ok) {
          let errorMessage = '삭제에 실패했습니다.';
          try {
            const contentType = response.headers.get('content-type');
            if (contentType && contentType.includes('application/json')) {
              const error = await response.json();
              errorMessage = error.error || errorMessage;
            } else {
              const errorText = await response.text();
              console.error('게시물 삭제 실패:', {
                status: response.status,
                statusText: response.statusText,
                errorText
              });
              if (response.status === 404) {
                errorMessage = '게시물을 찾을 수 없습니다.';
              } else if (response.status === 403) {
                errorMessage = '삭제 권한이 없습니다.';
              } else if (response.status === 401) {
                errorMessage = '로그인이 필요합니다.';
              }
            }
          } catch (parseError) {
            console.error('에러 응답 파싱 실패:', parseError);
          }
          alert(errorMessage);
          return;
        }

        alert('게시물이 삭제되었습니다.');
        onPostUpdated?.();
      } catch (error) {
        console.error('게시물 삭제 실패:', error);
        alert('네트워크 오류가 발생했습니다.');
      }
    }
  };

  // 공유 기능
  const handleSharePost = async (postId: number) => {
    try {
      const response = await fetch(apiConfig.endpoints.postShare(postId), {
        method: 'POST'
      });
      
      if (response.ok) {
        const data = await response.json();
        setShareUrl(data.share_url);
        
        // 클립보드에 복사
        await navigator.clipboard.writeText(data.share_url);
        alert('공유 링크가 클립보드에 복사되었습니다!');
        
        // 공유된 게시물을 모달로 열기
        const post = posts.find(p => p.id === postId);
        if (post) {
          openModal(post);
        }
      } else {
        alert('공유 링크 생성에 실패했습니다.');
      }
    } catch (error) {
      console.error('공유 실패:', error);
      alert('공유 기능을 사용할 수 없습니다.');
    }
  };

  // 이미지 스포일러 해제 함수
  const revealImage = (postId: number) => {
    setRevealedImages(prev => ({ ...prev, [postId]: true }));
  };

  // 모달 열기
  const openModal = (post: Post) => {
    setSelectedPost(post);
    setShowModal(true);
    // 뒷화면 스크롤 방지
    document.body.style.overflow = 'hidden';
    // 댓글 로드
    if (!comments[post.id]) {
      loadComments(post.id);
    }
  };

  // 모달 닫기
  const closeModal = () => {
    setShowModal(false);
    setSelectedPost(null);
    // 뒷화면 스크롤 복원
    document.body.style.overflow = 'unset';
  };

  // 댓글 수 로드
  const loadCommentCount = async (postId: number) => {
    try {
      const response = await fetch(apiConfig.endpoints.postComments(postId));
      if (response.ok) {
        const commentsData = await response.json();
        setCommentCounts(prev => ({
          ...prev,
          [postId]: commentsData.length
        }));
      }
    } catch (error) {
      console.error('댓글 수 로드 실패:', error);
    }
  };

  // 댓글 로드
  const loadComments = async (postId: number) => {
    setLoadingComments(prev => ({ ...prev, [postId]: true }));
    try {
      const response = await fetch(apiConfig.endpoints.postComments(postId));
      if (response.ok) {
        const commentsData = await response.json();
        setComments(prev => ({ ...prev, [postId]: commentsData }));
        setCommentCounts(prev => ({ ...prev, [postId]: commentsData.length }));
      }
    } catch (error) {
      console.error('댓글 로드 실패:', error);
    } finally {
      setLoadingComments(prev => ({ ...prev, [postId]: false }));
    }
  };

  const updatePost = async (postId: number, diaryText: string) => {
    try {
      const response = await fetch(apiConfig.endpoints.post(postId), {
        method: 'PUT',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ diary_text: diaryText })
      });
      
      // 응답 상태 체크
      if (!response.ok) {
        let errorMessage = '수정에 실패했습니다.';
        try {
          const contentType = response.headers.get('content-type');
          if (contentType && contentType.includes('application/json')) {
            const error = await response.json();
            errorMessage = error.error || errorMessage;
          } else {
            const errorText = await response.text();
            console.error('게시물 수정 실패:', {
              status: response.status,
              statusText: response.statusText,
              errorText
            });
            if (response.status === 404) {
              errorMessage = '게시물을 찾을 수 없습니다.';
            } else if (response.status === 403) {
              errorMessage = '수정 권한이 없습니다.';
            } else if (response.status === 401) {
              errorMessage = '로그인이 필요합니다.';
            }
          }
        } catch (parseError) {
          console.error('에러 응답 파싱 실패:', parseError);
        }
        alert(errorMessage);
        return;
      }

      alert('게시물이 수정되었습니다.');
      onPostUpdated?.();
    } catch (error) {
      console.error('게시물 수정 실패:', error);
      alert('네트워크 오류가 발생했습니다.');
    }
  };

  
  // 좋아요 상태 초기화 (posts가 변경될 때마다)
  useEffect(() => {
    const initialLikedState: Record<number, boolean> = {};
    const initialLikesCount: Record<number, number> = {};
    posts.forEach(post => {
      initialLikedState[post.id] = Boolean(post.liked);
      initialLikesCount[post.id] = post.likes_count || 0;
      
      // 디버깅을 위한 로그
      if (post.liked) {
        console.log(`게시물 ${post.id} 좋아요 상태:`, {
          liked: post.liked,
          likes_count: post.likes_count,
          post_id: post.id
        });
      }
    });
    setLikedPosts(initialLikedState);
    setLikesCount(initialLikesCount);
    
    console.log('좋아요 상태 초기화 완료:', {
      initialLikedState,
      initialLikesCount,
      postsCount: posts.length
    });
  }, [posts]);

  // 좋아요 토글 함수
  const toggleLike = async (postId: number) => {
    // 중복 요청 방지 - 더 강력한 체크
    if (submittingComment[postId]) {
      console.log('좋아요 요청 중복 방지:', postId);
      return;
    }

    try {
      // 로딩 상태 설정
      setSubmittingComment(prev => ({ ...prev, [postId]: true }));

      const headers: HeadersInit = {
        'Content-Type': 'application/json'
      };
      
      // 사용자 ID 헤더 추가
      const currentUserId = getCurrentUserId();
      if (currentUserId) {
        headers['X-Encrypted-Yw-ID'] = currentUserId;
        headers['X-Is-Login'] = '1';
      }
      
      console.log('좋아요 요청 시작:', {
        postId,
        currentUserId,
        currentLikedState: likedPosts[postId]
      });
      
      const response = await fetch(apiConfig.endpoints.postLike(postId), {
        method: 'POST',
        headers
      });

      // 응답 상태 체크
      if (!response.ok) {
        const errorText = await response.text();
        console.error('좋아요 처리 실패:', {
          status: response.status,
          statusText: response.statusText,
          errorText
        });
        
        if (response.status === 404) {
          alert('게시물을 찾을 수 없습니다.');
        } else if (response.status === 401) {
          alert('로그인이 필요합니다.');
        } else {
          alert('좋아요 처리에 실패했습니다.');
        }
        return;
      }

      // Content-Type 체크
      const contentType = response.headers.get('content-type');
      if (!contentType || !contentType.includes('application/json')) {
        const responseText = await response.text();
        console.error('예상하지 못한 응답 형식:', {
          contentType,
          responseText
        });
        alert('서버 응답 형식이 올바르지 않습니다.');
        return;
      }

      const data = await response.json();
      
      if (data.success) {
        // 좋아요 상태 즉시 업데이트
        setLikedPosts(prev => ({ ...prev, [postId]: data.liked }));
        
        // 좋아요 수 업데이트 - 현재 상태를 정확히 반영
        setLikesCount(prev => {
          const currentCount = prev[postId] || posts.find(p => p.id === postId)?.likes_count || 0;
          const newCount = data.liked ? currentCount + 1 : Math.max(0, currentCount - 1);
          
          console.log('좋아요 수 업데이트:', {
            postId,
            currentCount,
            newCount,
            liked: data.liked,
            message: data.message
          });
          
          return { ...prev, [postId]: newCount };
        });
        
        console.log('좋아요 상태 업데이트 완료:', {
          postId,
          liked: data.liked,
          message: data.message
        });
      } else {
        console.error('좋아요 처리 실패:', data);
        alert(data.error || '좋아요 처리에 실패했습니다.');
      }
      
    } catch (error) {
      console.error('좋아요 처리 실패:', error);
      alert('네트워크 오류가 발생했습니다.');
    } finally {
      // 로딩 상태 해제
      setSubmittingComment(prev => ({ ...prev, [postId]: false }));
    }
  };

  // 공유 기능
  const sharePost = async (post: Post) => {
    try {
      const response = await fetch(apiConfig.endpoints.postShare(post.id), {
        method: 'POST'
      });
      
      if (response.ok) {
        const data = await response.json();
        
        // 클립보드에 복사
        await navigator.clipboard.writeText(data.share_url);
        alert('공유 링크가 클립보드에 복사되었습니다!');
        
        // 공유된 게시물을 모달로 열기
        openModal(post);
      } else {
        alert('공유 링크 생성에 실패했습니다.');
      }
    } catch (error) {
      console.error('공유 실패:', error);
      alert('공유 기능을 사용할 수 없습니다.');
    }
  };

  // 댓글 토글 함수
  const toggleComments = async (postId: number) => {
    const isExpanded = expandedComments[postId];
    
    if (isExpanded) {
      setExpandedComments(prev => ({ ...prev, [postId]: false }));
    } else {
      setExpandedComments(prev => ({ ...prev, [postId]: true }));
      
      if (!comments[postId]) {
        setLoadingComments(prev => ({ ...prev, [postId]: true }));
        try {
          const response = await fetch(apiConfig.endpoints.postComments(postId));
          
          if (!response.ok) {
            console.error('댓글 로딩 실패:', {
              status: response.status,
              statusText: response.statusText
            });
            return;
          }

          // Content-Type 체크
          const contentType = response.headers.get('content-type');
          if (!contentType || !contentType.includes('application/json')) {
            const responseText = await response.text();
            console.error('예상하지 못한 응답 형식:', {
              contentType,
              responseText
            });
            return;
          }

          const data = await response.json();
          setComments(prev => ({ ...prev, [postId]: data }));
        } catch (error) {
          console.error('댓글 로딩 실패:', error);
        } finally {
          setLoadingComments(prev => ({ ...prev, [postId]: false }));
        }
      }
    }
  };

  // 댓글 제출 함수
  const submitComment = async (postId: number) => {
    const content = newComment[postId]?.trim();
    if (!content) return;

    setSubmittingComment(prev => ({ ...prev, [postId]: true }));
    try {
      const headers: HeadersInit = {
        'Content-Type': 'application/json'
      };
      
      // 사용자 ID 헤더 추가
      const currentUserId = getCurrentUserId();
      if (currentUserId) {
        headers['X-Encrypted-Yw-ID'] = currentUserId;
        headers['X-Is-Login'] = '1';
      }
      
      const response = await fetch(apiConfig.endpoints.postComments(postId), {
        method: 'POST',
        headers,
        body: JSON.stringify({
          content,
          display_name: userInfo?.display_name || anonymousProfile?.displayName || '익명',
          photo_url: userInfo?.photo_url || (anonymousProfile?.avatarEmoji ? `emoji:${anonymousProfile.avatarEmoji}` : null),
          avatar_color: anonymousProfile?.avatarColor || 'from-purple-400 to-pink-400'
        })
      });

      // 응답 상태 체크
      if (!response.ok) {
        let errorMessage = '댓글 등록에 실패했습니다.';
        try {
          const contentType = response.headers.get('content-type');
          if (contentType && contentType.includes('application/json')) {
            const errorData = await response.json();
            errorMessage = errorData.error || errorMessage;
          } else {
            const errorText = await response.text();
            console.error('댓글 등록 실패:', {
              status: response.status,
              statusText: response.statusText,
              errorText
            });
            if (response.status === 404) {
              errorMessage = '게시물을 찾을 수 없습니다.';
            } else if (response.status === 401) {
              errorMessage = '로그인이 필요합니다.';
            }
          }
        } catch (parseError) {
          console.error('에러 응답 파싱 실패:', parseError);
        }
        alert(errorMessage);
        return;
      }

      // 댓글 목록 새로고침
      const commentsResponse = await fetch(apiConfig.endpoints.postComments(postId));
      if (commentsResponse.ok) {
        const contentType = commentsResponse.headers.get('content-type');
        if (contentType && contentType.includes('application/json')) {
          const data = await commentsResponse.json();
          setComments(prev => ({ ...prev, [postId]: data }));
          setNewComment(prev => ({ ...prev, [postId]: '' }));
        }
      }
    } catch (error) {
      console.error('댓글 등록 실패:', error);
      alert('댓글 등록에 실패했습니다.');
    } finally {
      setSubmittingComment(prev => ({ ...prev, [postId]: false }));
    }
  };

  // 대댓글 제출 함수
  const submitReply = async (postId: number, parentCommentId: number) => {
    const content = newReply[parentCommentId]?.trim();
    if (!content) return;

    setSubmittingComment(prev => ({ ...prev, [postId]: true }));
    try {
      const headers: HeadersInit = {
        'Content-Type': 'application/json'
      };
      
      // 사용자 ID 헤더 추가
      const currentUserId = getCurrentUserId();
      if (currentUserId) {
        headers['X-Encrypted-Yw-ID'] = currentUserId;
        headers['X-Is-Login'] = '1';
      }
      
      const response = await fetch(apiConfig.endpoints.postComments(postId), {
        method: 'POST',
        headers,
        body: JSON.stringify({
          content,
          display_name: userInfo?.display_name || anonymousProfile?.displayName || '익명',
          photo_url: userInfo?.photo_url || (anonymousProfile?.avatarEmoji ? `emoji:${anonymousProfile.avatarEmoji}` : null),
          avatar_color: anonymousProfile?.avatarColor || 'from-purple-400 to-pink-400',
          parent_comment_id: parentCommentId
        })
      });

      if (response.ok) {
        // 댓글 목록 새로고침
        const commentsResponse = await fetch(apiConfig.endpoints.postComments(postId));
        if (commentsResponse.ok) {
          const contentType = commentsResponse.headers.get('content-type');
          if (contentType && contentType.includes('application/json')) {
            const data = await commentsResponse.json();
            setComments(prev => ({ ...prev, [postId]: data }));
            setNewReply(prev => ({ ...prev, [parentCommentId]: '' }));
            setReplyingTo(prev => ({ ...prev, [postId]: null }));
          }
        }
      }
    } catch (error) {
      console.error('대댓글 등록 실패:', error);
      alert('대댓글 등록에 실패했습니다.');
    } finally {
      setSubmittingComment(prev => ({ ...prev, [postId]: false }));
    }
  };

  if (posts.length === 0) {
    return (
      <div className="text-center py-12">
        <div className="text-6xl mb-4">📱</div>
        <h3 className="text-lg font-semibold text-gray-900 mb-2">아직 똥이 없어요</h3>
        <p className="text-gray-500 text-sm">똥을 공유하면 여기에 나타날 거예요.</p>
      </div>
    );
  }

  // 사용자 시간대 기반 시간 포맷팅 (개선된 버전)
  const formatDate = (dateString: string) => {
    try {
      // 백엔드에서 UTC로 저장된 시간을 올바르게 파싱
      let date: Date;
      
      // 디버깅을 위한 로그
      console.log('formatDate 입력:', {
        dateString,
        originalType: typeof dateString,
        includesT: dateString.includes('T'),
        includesZ: dateString.includes('Z')
      });
      
      // ISO 8601 형식인지 확인 (UTC 시간)
      if (dateString.includes('T') && dateString.includes('Z')) {
        // 이미 UTC 시간이므로 그대로 사용
        date = new Date(dateString);
      } else if (dateString.includes('T')) {
        // ISO 형식이지만 Z가 없는 경우 UTC로 처리
        date = new Date(dateString + 'Z');
      } else {
        // 기존 형식인 경우 로컬 시간으로 처리
        date = new Date(dateString);
      }
      
      // 유효하지 않은 날짜 체크
      if (isNaN(date.getTime())) {
        console.error('유효하지 않은 날짜:', dateString);
        return '시간 정보 없음';
      }
      
      const now = new Date();
      const diffInMs = now.getTime() - date.getTime();
      
      // 음수 시간 차이 체크 (미래 시간)
      if (diffInMs < 0) {
        return '방금 전';
      }
      
      const diffInSeconds = Math.floor(diffInMs / 1000);
      const diffInMinutes = Math.floor(diffInSeconds / 60);
      const diffInHours = Math.floor(diffInMinutes / 60);
      const diffInDays = Math.floor(diffInHours / 24);
      const diffInWeeks = Math.floor(diffInDays / 7);
      const diffInMonths = Math.floor(diffInDays / 30);
      const diffInYears = Math.floor(diffInDays / 365);

      console.log('formatDate 시간 차이 계산:', {
        diffInMs,
        diffInSeconds,
        diffInMinutes,
        diffInHours,
        diffInDays,
        diffInWeeks,
        diffInMonths,
        diffInYears
      });
      
      // 사용자의 현재 시간대 정보 가져오기
      const userTimezone = Intl.DateTimeFormat().resolvedOptions().timeZone;
      
      // 1분 미만: 방금 전
      if (diffInSeconds < 60) return '방금 전';
      
      // 1시간 미만: 분 단위
      if (diffInMinutes < 60) return `${diffInMinutes}분 전`;
      
      // 24시간 미만: 시간 단위
      if (diffInHours < 24) return `${diffInHours}시간 전`;
      
      // 7일 미만: 일 단위
      if (diffInDays < 7) return `${diffInDays}일 전`;
      
      // 4주 미만: 주 단위
      if (diffInWeeks < 4) return `${diffInWeeks}주 전`;
      
      // 12개월 미만: 월 단위
      if (diffInMonths < 12) return `${diffInMonths}개월 전`;
      
      // 그 이상: 년 단위
      if (diffInYears >= 1) return `${diffInYears}년 전`;
      
      // 그 이상: 사용자 시간대로 정확한 날짜/시간 표시
      const formattedTime = date.toLocaleString('ko-KR', {
        timeZone: userTimezone,
        year: 'numeric',
        month: 'short',
        day: 'numeric',
        hour: '2-digit',
        minute: '2-digit',
        hour12: false
      });
      
      console.log('formatDate 최종 결과:', formattedTime);
      return formattedTime;
      
    } catch (error) {
      console.error('formatDate 오류:', error);
      return '시간 정보 오류';
    }
  };

  // 상세 시간 표시용 함수 (모달에서 사용)
  const formatDetailedDate = (dateString: string) => {
    // 백엔드에서 UTC로 저장된 시간을 올바르게 파싱
    let date: Date;
    
    // ISO 8601 형식인지 확인 (UTC 시간)
    if (dateString.includes('T') && dateString.includes('Z')) {
      // 이미 UTC 시간이므로 그대로 사용
      date = new Date(dateString);
    } else if (dateString.includes('T')) {
      // ISO 형식이지만 Z가 없는 경우 UTC로 처리
      date = new Date(dateString + 'Z');
    } else {
      // 기존 형식인 경우 로컬 시간으로 처리
      date = new Date(dateString);
    }
    
    const userTimezone = Intl.DateTimeFormat().resolvedOptions().timeZone;
    
    return date.toLocaleString('ko-KR', {
      timeZone: userTimezone,
      year: 'numeric',
      month: 'long',
      day: 'numeric',
      hour: '2-digit',
      minute: '2-digit',
      second: '2-digit',
      hour12: false,
      weekday: 'short'
    });
  };

  return (
    <div className="space-y-0 pb-20">
      {posts.map((post, index) => (
        <AnimatedContent 
          key={post.id}
          delay={index * 0.1}
          distance={50}
          duration={0.8}
          direction="vertical"
        >
          <article
            className="bg-white border-b border-gray-100 last:border-b-0"
          >
          {/* 작성자 정보 헤더 */}
          <div className="flex items-center justify-between p-4">
            <div className="flex items-center space-x-3">
              <div className="w-8 h-8 rounded-full overflow-hidden">
                {post.photo_url ? (
                  post.photo_url.startsWith('emoji:') ? (
                    <div className={`w-full h-full bg-gradient-to-br ${post.avatar_color || 'from-purple-400 to-pink-400'} flex items-center justify-center text-white font-semibold text-sm`}>
                      {post.photo_url.replace('emoji:', '')}
                    </div>
                  ) : (
                    <img
                      src={post.photo_url}
                      alt="프로필"
                      className="w-full h-full object-cover"
                    />
                  )
                ) : (
                  <div className="w-full h-full bg-gradient-to-br from-purple-400 to-pink-400 flex items-center justify-center text-white font-semibold text-sm">
                    {post.display_name?.[0] || 'U'}
                  </div>
                )}
              </div>
              <div className="flex items-center space-x-2">
                <span className="font-semibold text-gray-900 text-sm">
                  {post.display_name || '익명'}
                </span>
                <span className="text-gray-400">•</span>
                <TimeTooltip content={formatDetailedDate(post.created_at)}>
                  <span className="text-gray-500 text-sm">
                    {formatDate(post.created_at)}
                  </span>
                </TimeTooltip>
              </div>
            </div>
            <div className="flex items-center space-x-2">
              {/* 수정/삭제 메뉴 (내 게시물인 경우만) */}
              {isMyPost(post) && (
                <PostMenu 
                  postId={post.id} 
                  post={post}
                  onEdit={() => handleEditPost(post)} 
                  onDelete={() => handleDeletePost(post.id)}
                  onPostUpdated={onPostUpdated}
                />
              )}
            </div>
          </div>

          {/* 이미지 */}
          <div 
            className="relative w-full aspect-square cursor-pointer"
            onClick={() => openModal(post)}
          >
            {post.image_url ? (
              <div className="relative w-full h-full">
                <img
                  src={post.image_url}
                  alt="게시물 이미지"
                  className={`w-full h-full object-cover transition-all duration-300 ${
                    revealedImages[post.id] ? 'blur-none' : 'blur-lg'
                  }`}
                  onError={(e) => {
                    const target = e.target as HTMLImageElement;
                    target.style.display = 'none';
                  }}
                />
                
                {/* 스포일러 오버레이 */}
                {!revealedImages[post.id] && (
                  <div 
                    className="absolute inset-0 bg-black bg-opacity-50 flex items-center justify-center cursor-pointer"
                    onClick={(e) => {
                      e.stopPropagation();
                      revealImage(post.id);
                    }}
                  >
                    <div className="text-center text-white">
                      <div className="w-16 h-16 mx-auto mb-3 bg-white bg-opacity-20 rounded-full flex items-center justify-center">
                        <svg className="w-8 h-8" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M15 12a3 3 0 11-6 0 3 3 0 016 0z" />
                          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M2.458 12C3.732 7.943 7.523 5 12 5c4.478 0 8.268 2.943 9.542 7-1.274 4.057-5.064 7-9.542 7-4.477 0-8.268-2.943-9.542-7z" />
                        </svg>
                      </div>
                      <p className="text-sm font-medium mb-1">스포일러</p>
                      <p className="text-xs opacity-80">클릭하여 이미지 보기</p>
                    </div>
                  </div>
                )}
              </div>
            ) : (
              <div className="w-full h-full bg-gray-100 flex items-center justify-center">
                <div className="text-center">
                  <svg className="w-12 h-12 text-gray-400 mx-auto mb-2" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M4 16l4.586-4.586a2 2 0 012.828 0L16 16m-2-2l1.586-1.586a2 2 0 012.828 0L20 14m-6-6h.01M6 20h12a2 2 0 002-2V6a2 2 0 00-2-2H6a2 2 0 00-2 2v12a2 2 0 002 2z" />
                  </svg>
                  <p className="text-gray-500 text-sm">Loading image...</p>
                </div>
              </div>
            )}
            
            {/* 댓글 수 표시 */}
            {commentCounts[post.id] > 0 && (
              <div className="absolute bottom-2 right-2 bg-black bg-opacity-50 text-white px-2 py-1 rounded-full text-xs flex items-center space-x-1">
                <svg className="w-3 h-3" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M8 12h.01M12 12h.01M16 12h.01M21 12c0 4.418-4.03 8-9 8a9.863 9.863 0 01-4.255-.949L3 20l1.395-3.72C3.512 15.042 3 13.574 3 12c0-4.418 4.03-8 9-8s9 3.582 9 8z" />
                </svg>
                <span>{commentCounts[post.id]}</span>
              </div>
            )}
          </div>

          {/* 액션 버튼들 */}
          <div className="p-4">
            <div className="flex items-center justify-between mb-3">
              <div className="flex items-center space-x-4">
                <button
                  onClick={() => {
                    console.log('좋아요 버튼 클릭:', {
                      postId: post.id,
                      currentLikedState: likedPosts[post.id],
                      postLiked: post.liked,
                      submitting: submittingComment[post.id]
                    });
                    toggleLike(post.id);
                  }}
                  disabled={submittingComment[post.id]}
                  className="p-1 hover:scale-110 transition-transform disabled:opacity-50 disabled:cursor-not-allowed"
                >
                  {submittingComment[post.id] ? (
                    <div className="w-6 h-6 flex items-center justify-center">
                      <div className="animate-spin rounded-full h-4 w-4 border-2 border-gray-300 border-t-gray-600"></div>
                    </div>
                  ) : likedPosts[post.id] ? (
                    <svg 
                      className="w-6 h-6 text-red-500" 
                      fill="currentColor" 
                      viewBox="0 0 20 20"
                    >
                      <title>좋아요됨 ({likesCount[post.id] || 0})</title>
                      <path fillRule="evenodd" d="M3.172 5.172a4 4 0 015.656 0L10 6.343l1.172-1.171a4 4 0 115.656 5.656L10 17.657l-6.828-6.829a4 4 0 010-5.656z" clipRule="evenodd" />
                    </svg>
                  ) : (
                    <svg className="w-6 h-6 text-gray-900" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M4.318 6.318a4.5 4.5 0 000 6.364L12 20.364l7.682-7.682a4.5 4.5 0 00-6.364-6.364L12 7.636l-1.318-1.318a4.5 4.5 0 00-6.364 0z" />
                    </svg>
                  )}
                </button>
                <button
                  onClick={() => toggleComments(post.id)}
                  className="p-1 hover:scale-110 transition-transform"
                >
                  <svg className="w-6 h-6 text-gray-900" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M8 12h.01M12 12h.01M16 12h.01M21 12c0 4.418-4.03 8-9 8a9.863 9.863 0 01-4.255-.949L3 20l1.395-3.72C3.512 15.042 3 13.574 3 12c0-4.418 4.03-8 9-8s9 3.582 9 8z" />
                  </svg>
                </button>
                <button 
                  onClick={() => sharePost(post)}
                  className="p-1 hover:scale-110 transition-transform"
                >
                  <svg className="w-6 h-6 text-gray-900" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M8.684 13.342C8.886 12.938 9 12.482 9 12c0-.482-.114-.938-.316-1.342m0 2.684a3 3 0 110-2.684m0 2.684l6.632 3.316m-6.632-6l6.632-3.316m0 0a3 3 0 105.367-2.684 3 3 0 00-5.367 2.684zm0 9.316a3 3 0 105.367 2.684 3 3 0 00-5.367-2.684z" />
                  </svg>
                </button>
              </div>
            </div>

            {/* 좋아요 수 */}
            <div className="mb-2">
              <span 
                className="font-semibold text-gray-900 text-sm"
                title={`좋아요 상태: ${likedPosts[post.id] ? '좋아요됨' : '좋아요 안됨'}, 로컬 카운트: ${likesCount[post.id]}, 서버 카운트: ${post.likes_count}`}
              >
                {(likesCount[post.id] !== undefined ? likesCount[post.id] : post.likes_count) || 0} likes
              </span>
            </div>

            {/* 캡션 */}
            <div className="mb-2">
              <span className="font-semibold text-gray-900 text-sm mr-2">
                {post.display_name || '익명'}
              </span>
              <span className="text-gray-900 text-sm">
                {post.diary_text}
              </span>
            </div>

            {/* 댓글 보기 버튼 */}
            {comments[post.id]?.length > 0 && (
              <button
                onClick={() => toggleComments(post.id)}
                className="text-gray-500 text-sm mb-2 block"
              >
                View all {comments[post.id].length} comments
              </button>
            )}
          </div>

          {/* 댓글 섹션 */}
          {expandedComments[post.id] && (
            <div className="px-4 pb-4 border-t border-gray-100">
              {/* 댓글 목록 */}
              <div className="space-y-3 mt-4 mb-4">
                {loadingComments[post.id] ? (
                  <div className="flex justify-center py-4">
                    <div className="animate-spin rounded-full h-5 w-5 border-2 border-gray-300 border-t-gray-600"></div>
                  </div>
                ) : comments[post.id]?.length > 0 ? (
                  comments[post.id].map((comment) => (
                    <div key={comment.id}>
                      {/* 부모 댓글 */}
                      <div className="flex items-start space-x-3">
                        <div className="w-8 h-8 rounded-full overflow-hidden flex-shrink-0">
                          {comment.photo_url ? (
                            comment.photo_url.startsWith('emoji:') ? (
                              <div className={`w-full h-full bg-gradient-to-br ${comment.avatar_color || 'from-purple-400 to-pink-400'} flex items-center justify-center text-white font-semibold text-xs`}>
                                {comment.photo_url.replace('emoji:', '')}
                              </div>
                            ) : (
                              <img
                                src={comment.photo_url}
                                alt="프로필"
                                className="w-full h-full object-cover"
                              />
                            )
                          ) : (
                            <div className="w-full h-full bg-gradient-to-br from-purple-400 to-pink-400 flex items-center justify-center text-white font-semibold text-xs">
                              {comment.display_name?.[0] || 'U'}
                            </div>
                          )}
                        </div>
                        <div className="flex-1 min-w-0">
                          <div className="flex items-start justify-between">
                            <div className="flex-1 min-w-0">
                              <p className="text-sm text-gray-900">
                                <span className="font-semibold mr-2">
                                  {comment.display_name || '익명'}
                                </span>
                                {comment.content}
                              </p>
                              <div className="flex items-center space-x-3 mt-1">
                                <TimeTooltip content={formatDetailedDate(comment.created_at)}>
                                  <p className="text-xs text-gray-500">
                                    {formatDate(comment.created_at)}
                                  </p>
                                </TimeTooltip>
                                <button
                                  onClick={() => setReplyingTo(prev => ({ ...prev, [post.id]: prev[post.id] === comment.id ? null : comment.id }))}
                                  className="text-xs text-gray-500 hover:text-blue-500 transition-colors"
                                >
                                  답글 달기
                                </button>
                              </div>
                            </div>
                            {/* 댓글 수정/삭제 버튼 (내 댓글인 경우만) */}
                            {isMyComment(comment) && (
                              <div className="flex items-center space-x-1 ml-2">
                                <button
                                  onClick={() => handleEditComment(post.id, comment.id, comment.content)}
                                  className="text-gray-400 hover:text-blue-500 transition-colors"
                                  title="댓글 수정"
                                >
                                  <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M11 5H6a2 2 0 00-2 2v11a2 2 0 002 2h11a2 2 0 002-2v-5m-1.414-9.414a2 2 0 112.828 2.828L11.828 15H9v-2.828l8.586-8.586z" />
                                  </svg>
                                </button>
                                <button
                                  onClick={() => handleDeleteComment(comment.id, post.id)}
                                  className="text-gray-400 hover:text-red-500 transition-colors"
                                  title="댓글 삭제"
                                >
                                  <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 7l-.867 12.142A2 2 0 0116.138 21H7.862a2 2 0 01-1.995-1.858L5 7m5 4v6m4-6v6m1-10V4a1 1 0 00-1-1h-4a1 1 0 00-1 1v3M4 7h16" />
                                  </svg>
                                </button>
                              </div>
                            )}
                          </div>
                        </div>
                      </div>
                      
                      {/* 대댓글들 */}
                      {comment.replies && comment.replies.length > 0 && (
                        <div className="ml-11 mt-2 space-y-2">
                          {comment.replies.map((reply) => (
                            <div key={reply.id}>
                              {/* 대댓글 */}
                              <div className="flex items-start space-x-3">
                                <div className="w-6 h-6 rounded-full overflow-hidden flex-shrink-0">
                                  {reply.photo_url ? (
                                    reply.photo_url.startsWith('emoji:') ? (
                                      <div className={`w-full h-full bg-gradient-to-br ${reply.avatar_color || 'from-purple-400 to-pink-400'} flex items-center justify-center text-white font-semibold text-xs`}>
                                        {reply.photo_url.replace('emoji:', '')}
                                      </div>
                                    ) : (
                                      <img
                                        src={reply.photo_url}
                                        alt="프로필"
                                        className="w-full h-full object-cover"
                                      />
                                    )
                                  ) : (
                                    <div className="w-full h-full bg-gradient-to-br from-purple-400 to-pink-400 flex items-center justify-center text-white font-semibold text-xs">
                                      {reply.display_name?.[0] || 'U'}
                                    </div>
                                  )}
                                </div>
                                <div className="flex-1 min-w-0">
                                  <div className="flex items-start justify-between">
                                    <div className="flex-1 min-w-0">
                                      <p className="text-sm text-gray-900">
                                        <span className="font-semibold mr-2">
                                          {reply.display_name || '익명'}
                                        </span>
                                        {reply.content}
                                      </p>
                                      <div className="flex items-center space-x-3 mt-1">
                                        <TimeTooltip content={formatDetailedDate(reply.created_at)}>
                                          <p className="text-xs text-gray-500">
                                            {formatDate(reply.created_at)}
                                          </p>
                                        </TimeTooltip>
                                        <button
                                          onClick={() => setReplyingTo(prev => ({ ...prev, [post.id]: prev[post.id] === reply.id ? null : reply.id }))}
                                          className="text-xs text-gray-500 hover:text-blue-500 transition-colors"
                                        >
                                          답글 달기
                                        </button>
                                      </div>
                                    </div>
                                    {/* 대댓글 수정/삭제 버튼 (내 댓글인 경우만) */}
                                    {isMyComment(reply) && (
                                      <div className="flex items-center space-x-1 ml-2">
                                        <button
                                          onClick={() => handleEditComment(post.id, reply.id, reply.content)}
                                          className="text-gray-400 hover:text-blue-500 transition-colors"
                                          title="댓글 수정"
                                        >
                                          <svg className="w-3 h-3" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M11 5H6a2 2 0 00-2 2v11a2 2 0 002 2h11a2 2 0 002-2v-5m-1.414-9.414a2 2 0 112.828 2.828L11.828 15H9v-2.828l8.586-8.586z" />
                                          </svg>
                                        </button>
                                        <button
                                          onClick={() => handleDeleteComment(reply.id, post.id)}
                                          className="text-gray-400 hover:text-red-500 transition-colors"
                                          title="댓글 삭제"
                                        >
                                          <svg className="w-3 h-3" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 7l-.867 12.142A2 2 0 0116.138 21H7.862a2 2 0 01-1.995-1.858L5 7m5 4v6m4-6v6m1-10V4a1 1 0 00-1-1h-4a1 1 0 00-1 1v3M4 7h16" />
                                          </svg>
                                        </button>
                                      </div>
                                    )}
                                  </div>
                                </div>
                              </div>
                              
                              {/* 대댓글에 대한 답글 입력 필드 */}
                              {replyingTo[post.id] === reply.id && (
                                <div className="ml-9 mt-2">
                                  <div className="flex items-center space-x-2">
                                    <div className="w-6 h-6 rounded-full overflow-hidden">
                                      {userInfo?.photo_url ? (
                                        userInfo.photo_url.startsWith('emoji:') ? (
                                          <div className={`w-full h-full bg-gradient-to-br ${anonymousProfile?.avatarColor || 'from-purple-400 to-pink-400'} flex items-center justify-center text-white font-semibold text-xs`}>
                                            {userInfo.photo_url.replace('emoji:', '')}
                                          </div>
                                        ) : (
                                          <img
                                            src={userInfo.photo_url}
                                            alt="프로필"
                                            className="w-full h-full object-cover"
                                          />
                                        )
                                      ) : (
                                        <div className={`w-full h-full bg-gradient-to-br ${anonymousProfile?.avatarColor || 'from-purple-400 to-pink-400'} flex items-center justify-center text-white font-semibold text-xs`}>
                                          {anonymousProfile?.avatarEmoji || (userInfo?.display_name || anonymousProfile?.displayName)?.[0] || 'U'}
                                        </div>
                                      )}
                                    </div>
                                    <input
                                      type="text"
                                      value={newReply[reply.id] || ''}
                                      onChange={(e) => setNewReply(prev => ({ ...prev, [reply.id]: e.target.value }))}
                                      placeholder="답글 추가..."
                                      className="flex-1 text-sm text-gray-900 placeholder-gray-500 border-none focus:outline-none"
                                      onKeyPress={(e) => {
                                        if (e.key === 'Enter') {
                                          submitReply(post.id, reply.id);
                                        }
                                      }}
                                    />
                                    <button
                                      onClick={() => submitReply(post.id, reply.id)}
                                      disabled={submittingComment[post.id] || !newReply[reply.id]?.trim()}
                                      className="text-blue-500 hover:text-blue-600 disabled:text-gray-300 disabled:cursor-not-allowed text-sm font-semibold"
                                    >
                                      게시
                                    </button>
                                  </div>
                                </div>
                              )}
                            </div>
                          ))}
                        </div>
                      )}
                      
                      {/* 답글 입력 필드 */}
                      {replyingTo[post.id] === comment.id && (
                        <div className="ml-11 mt-2">
                          <div className="flex items-center space-x-2">
                            <div className="w-6 h-6 rounded-full overflow-hidden">
                              {userInfo?.photo_url ? (
                                userInfo.photo_url.startsWith('emoji:') ? (
                                  <div className={`w-full h-full bg-gradient-to-br ${anonymousProfile?.avatarColor || 'from-purple-400 to-pink-400'} flex items-center justify-center text-white font-semibold text-xs`}>
                                    {userInfo.photo_url.replace('emoji:', '')}
                                  </div>
                                ) : (
                                  <img
                                    src={userInfo.photo_url}
                                    alt="프로필"
                                    className="w-full h-full object-cover"
                                  />
                                )
                              ) : (
                                <div className={`w-full h-full bg-gradient-to-br ${anonymousProfile?.avatarColor || 'from-purple-400 to-pink-400'} flex items-center justify-center text-white font-semibold text-xs`}>
                                  {anonymousProfile?.avatarEmoji || (userInfo?.display_name || anonymousProfile?.displayName)?.[0] || 'U'}
                                </div>
                              )}
                            </div>
                            <input
                              type="text"
                              value={newReply[comment.id] || ''}
                              onChange={(e) => setNewReply(prev => ({ ...prev, [comment.id]: e.target.value }))}
                              placeholder="답글 추가..."
                              className="flex-1 text-sm text-gray-900 placeholder-gray-500 border-none focus:outline-none"
                              onKeyPress={(e) => {
                                if (e.key === 'Enter') {
                                  submitReply(post.id, comment.id);
                                }
                              }}
                            />
                            <button
                              onClick={() => submitReply(post.id, comment.id)}
                              disabled={submittingComment[post.id] || !newReply[comment.id]?.trim()}
                              className="text-blue-500 hover:text-blue-600 disabled:text-gray-300 disabled:cursor-not-allowed text-sm font-semibold"
                            >
                              게시
                            </button>
                          </div>
                        </div>
                      )}
                    </div>
                  ))
                ) : (
                  <div className="text-center py-6 text-gray-500">
                    <p className="text-sm">아직 댓글이 없어요.</p>
                    <p className="text-xs">첫 댓글을 남겨보세요!</p>
                  </div>
                )}
              </div>

              {/* 댓글 입력 */}
              <div className="flex items-center space-x-3 pt-3 border-t border-gray-100">
                <div className="w-8 h-8 rounded-full overflow-hidden">
                  {userInfo?.photo_url ? (
                    userInfo.photo_url.startsWith('emoji:') ? (
                      <div className={`w-full h-full bg-gradient-to-br ${anonymousProfile?.avatarColor || 'from-purple-400 to-pink-400'} flex items-center justify-center text-white font-semibold text-xs`}>
                        {userInfo.photo_url.replace('emoji:', '')}
                      </div>
                    ) : (
                      <img
                        src={userInfo.photo_url}
                        alt="프로필"
                        className="w-full h-full object-cover"
                      />
                    )
                  ) : (
                    <div className={`w-full h-full bg-gradient-to-br ${anonymousProfile?.avatarColor || 'from-purple-400 to-pink-400'} flex items-center justify-center text-white font-semibold text-xs`}>
                      {anonymousProfile?.avatarEmoji || (userInfo?.display_name || anonymousProfile?.displayName)?.[0] || 'U'}
                    </div>
                  )}
                </div>
                <div className="flex-1 flex items-center space-x-2">
                  <input
                    type="text"
                    value={newComment[post.id] || ''}
                    onChange={(e) => setNewComment(prev => ({ ...prev, [post.id]: e.target.value }))}
                    placeholder="댓글 추가..."
                    className="flex-1 text-sm text-gray-900 placeholder-gray-500 border-none focus:outline-none"
                    maxLength={500}
                    onKeyPress={(e) => {
                      if (e.key === 'Enter' && !e.shiftKey) {
                        e.preventDefault();
                        submitComment(post.id);
                      }
                    }}
                  />
                  {newComment[post.id]?.trim() && (
                    <button
                      onClick={() => submitComment(post.id)}
                      disabled={submittingComment[post.id]}
                      className="text-blue-500 font-semibold text-sm hover:text-blue-600 disabled:opacity-50"
                    >
                      {submittingComment[post.id] ? '등록 중...' : '등록'}
                    </button>
                  )}
                </div>
              </div>
            </div>
          )}
          </article>
        </AnimatedContent>
      ))}
      
      {/* 게시물 모달 */}
      {showModal && selectedPost && (
        <div 
          className="fixed inset-0 bg-black bg-opacity-50 backdrop-blur-sm flex items-center justify-center z-50 p-4"
          onClick={closeModal}
          style={{ overflow: 'hidden' }}
        >
          <div 
            className="bg-white rounded-lg max-w-4xl w-full max-h-[90vh] overflow-hidden shadow-2xl"
            onClick={(e) => e.stopPropagation()}
          >
            {/* 모달 헤더 */}
            <div className="flex items-center justify-between p-4 border-b">
              <div className="flex items-center space-x-3">
                <div className="w-8 h-8 rounded-full overflow-hidden">
                  {selectedPost.photo_url ? (
                    selectedPost.photo_url.startsWith('emoji:') ? (
                      <div className={`w-full h-full bg-gradient-to-br ${selectedPost.avatar_color || 'from-purple-400 to-pink-400'} flex items-center justify-center text-white font-semibold text-sm`}>
                        {selectedPost.photo_url.replace('emoji:', '')}
                      </div>
                    ) : (
                      <img
                        src={selectedPost.photo_url}
                        alt="프로필"
                        className="w-full h-full object-cover"
                      />
                    )
                  ) : (
                    <div className="w-full h-full bg-gradient-to-br from-purple-400 to-pink-400 flex items-center justify-center text-white font-semibold text-sm">
                      {selectedPost.display_name?.[0] || 'U'}
                    </div>
                  )}
                </div>
                <div>
                  <span className="font-semibold text-gray-900 text-sm">
                    {selectedPost.display_name || '익명'}
                  </span>
                  <span className="text-gray-400 ml-2">•</span>
                  <TimeTooltip content={formatDetailedDate(selectedPost.created_at)}>
                    <span className="text-gray-500 text-sm ml-2">
                      {formatDate(selectedPost.created_at)}
                    </span>
                  </TimeTooltip>
                </div>
              </div>
              <div className="flex items-center space-x-2">
                {/* 공유 버튼 */}
                <button
                  onClick={() => handleSharePost(selectedPost.id)}
                  className="p-2 hover:bg-gray-100 rounded-full transition-colors"
                  title="공유"
                >
                  <svg className="w-5 h-5 text-gray-600" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M8.684 13.342C8.886 12.938 9 12.482 9 12c0-.482-.114-.938-.316-1.342m0 2.684a3 3 0 110-2.684m0 2.684l6.632 3.316m-6.632-6l6.632-3.316m0 0a3 3 0 105.367-2.684 3 3 0 00-5.367 2.684zm0 9.316a3 3 0 105.367 2.684 3 3 0 00-5.367-2.684z" />
                  </svg>
                </button>
                
                {/* 수정/삭제 메뉴 */}
                {isMyPost(selectedPost) && (
                  <PostMenu 
                    postId={selectedPost.id} 
                    post={selectedPost}
                    onEdit={() => handleEditPost(selectedPost)} 
                    onDelete={() => handleDeletePost(selectedPost.id)}
                    onPostUpdated={onPostUpdated}
                  />
                )}
                
                {/* 닫기 버튼 */}
                <button
                  onClick={closeModal}
                  className="p-2 hover:bg-gray-100 rounded-full transition-colors"
                  title="닫기"
                >
                  <svg className="w-5 h-5 text-gray-600" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
                  </svg>
                </button>
              </div>
            </div>
            
            {/* 모달 내용 */}
            <div className="flex flex-col md:flex-row max-h-[calc(90vh-80px)]">
              {/* 이미지 섹션 */}
              <div className="md:w-1/2 bg-gray-100 flex items-center justify-center">
                {selectedPost.image_url ? (
                  <div className="relative w-full h-full">
                    <img
                      src={selectedPost.image_url}
                      alt="게시물 이미지"
                      className={`w-full h-full object-contain transition-all duration-300 ${
                        revealedImages[selectedPost.id] ? 'blur-none' : 'blur-lg'
                      }`}
                      onError={(e) => {
                        const target = e.target as HTMLImageElement;
                        target.style.display = 'none';
                      }}
                    />
                    
                    {/* 스포일러 오버레이 */}
                    {!revealedImages[selectedPost.id] && (
                      <div 
                        className="absolute inset-0 bg-black bg-opacity-50 flex items-center justify-center cursor-pointer"
                        onClick={() => revealImage(selectedPost.id)}
                      >
                        <div className="text-center text-white">
                          <div className="w-20 h-20 mx-auto mb-4 bg-white bg-opacity-20 rounded-full flex items-center justify-center">
                            <svg className="w-10 h-10" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M15 12a3 3 0 11-6 0 3 3 0 016 0z" />
                              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M2.458 12C3.732 7.943 7.523 5 12 5c4.478 0 8.268 2.943 9.542 7-1.274 4.057-5.064 7-9.542 7-4.477 0-8.268-2.943-9.542-7z" />
                            </svg>
                          </div>
                          <p className="text-lg font-medium mb-2">스포일러</p>
                          <p className="text-sm opacity-80">클릭하여 이미지 보기</p>
                        </div>
                      </div>
                    )}
                  </div>
                ) : (
                  <div className="text-center">
                    <svg className="w-16 h-16 text-gray-400 mx-auto mb-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M4 16l4.586-4.586a2 2 0 012.828 0L16 16m-2-2l1.586-1.586a2 2 0 012.828 0L20 14m-6-6h.01M6 20h12a2 2 0 002-2V6a2 2 0 00-2-2H6a2 2 0 00-2 2v12a2 2 0 002 2z" />
                    </svg>
                    <p className="text-gray-500">이미지를 불러올 수 없습니다</p>
                  </div>
                )}
              </div>
              
              {/* 댓글 섹션 */}
              <div className="md:w-1/2 flex flex-col">
                {/* 게시물 텍스트 */}
                <div className="p-4 border-b">
                  <p className="text-gray-900 whitespace-pre-wrap">{selectedPost.diary_text}</p>
                </div>
                
                {/* 댓글 목록 */}
                <div className="flex-1 overflow-y-auto p-4 space-y-3">
                  {comments[selectedPost.id]?.map((comment) => (
                    <div key={comment.id}>
                      {/* 부모 댓글 */}
                      <div className="flex items-start space-x-3">
                        <div className="w-6 h-6 rounded-full overflow-hidden flex-shrink-0">
                          {comment.photo_url ? (
                            comment.photo_url.startsWith('emoji:') ? (
                              <div className={`w-full h-full bg-gradient-to-br ${comment.avatar_color || 'from-purple-400 to-pink-400'} flex items-center justify-center text-white font-semibold text-xs`}>
                                {comment.photo_url.replace('emoji:', '')}
                              </div>
                            ) : (
                              <img
                                src={comment.photo_url}
                                alt="프로필"
                                className="w-full h-full object-cover"
                              />
                            )
                          ) : (
                            <div className="w-full h-full bg-gradient-to-br from-purple-400 to-pink-400 flex items-center justify-center text-white font-semibold text-xs">
                              {comment.display_name?.[0] || 'U'}
                            </div>
                          )}
                        </div>
                        <div className="flex-1">
                          <div className="flex items-center space-x-2">
                            <span className="font-semibold text-gray-900 text-sm">
                              {comment.display_name || '익명'}
                            </span>
                            <TimeTooltip content={formatDetailedDate(comment.created_at)}>
                              <span className="text-gray-500 text-sm">
                                {formatDate(comment.created_at)}
                              </span>
                            </TimeTooltip>
                          </div>
                          <p className="text-gray-900 text-sm mt-1">{comment.content}</p>
                          <div className="flex items-center space-x-3 mt-1">
                            <button
                              onClick={() => setReplyingTo(prev => ({ ...prev, [selectedPost.id]: prev[selectedPost.id] === comment.id ? null : comment.id }))}
                              className="text-xs text-gray-500 hover:text-blue-500 transition-colors"
                            >
                              답글 달기
                            </button>
                            {/* 댓글 수정/삭제 버튼 (내 댓글인 경우만) */}
                            {isMyComment(comment) && (
                              <div className="flex items-center space-x-1">
                                <button
                                  onClick={() => handleEditComment(selectedPost.id, comment.id, comment.content)}
                                  className="text-gray-400 hover:text-blue-500 transition-colors"
                                  title="댓글 수정"
                                >
                                  <svg className="w-3 h-3" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M11 5H6a2 2 0 00-2 2v11a2 2 0 002 2h11a2 2 0 002-2v-5m-1.414-9.414a2 2 0 112.828 2.828L11.828 15H9v-2.828l8.586-8.586z" />
                                  </svg>
                                </button>
                                <button
                                  onClick={() => handleDeleteComment(comment.id, selectedPost.id)}
                                  className="text-gray-400 hover:text-red-500 transition-colors"
                                  title="댓글 삭제"
                                >
                                  <svg className="w-3 h-3" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 7l-.867 12.142A2 2 0 0116.138 21H7.862a2 2 0 01-1.995-1.858L5 7m5 4v6m4-6v6m1-10V4a1 1 0 00-1-1h-4a1 1 0 00-1 1v3M4 7h16" />
                                  </svg>
                                </button>
                              </div>
                            )}
                          </div>
                        </div>
                      </div>
                      
                      {/* 대댓글들 */}
                      {comment.replies && comment.replies.length > 0 && (
                        <div className="ml-9 mt-2 space-y-2">
                          {comment.replies.map((reply) => (
                            <div key={reply.id}>
                              {/* 대댓글 */}
                              <div className="flex items-start space-x-3">
                                <div className="w-5 h-5 rounded-full overflow-hidden flex-shrink-0">
                                  {reply.photo_url ? (
                                    reply.photo_url.startsWith('emoji:') ? (
                                      <div className={`w-full h-full bg-gradient-to-br ${reply.avatar_color || 'from-purple-400 to-pink-400'} flex items-center justify-center text-white font-semibold text-xs`}>
                                        {reply.photo_url.replace('emoji:', '')}
                                      </div>
                                    ) : (
                                      <img
                                        src={reply.photo_url}
                                        alt="프로필"
                                        className="w-full h-full object-cover"
                                      />
                                    )
                                  ) : (
                                    <div className="w-full h-full bg-gradient-to-br from-purple-400 to-pink-400 flex items-center justify-center text-white font-semibold text-xs">
                                      {reply.display_name?.[0] || 'U'}
                                    </div>
                                  )}
                                </div>
                                <div className="flex-1">
                                  <div className="flex items-center space-x-2">
                                    <span className="font-semibold text-gray-900 text-sm">
                                      {reply.display_name || '익명'}
                                    </span>
                                    <TimeTooltip content={formatDetailedDate(reply.created_at)}>
                                      <span className="text-gray-500 text-sm">
                                        {formatDate(reply.created_at)}
                                      </span>
                                    </TimeTooltip>
                                  </div>
                                  <p className="text-gray-900 text-sm mt-1">{reply.content}</p>
                                  <div className="flex items-center space-x-3 mt-1">
                                    <button
                                      onClick={() => setReplyingTo(prev => ({ ...prev, [selectedPost.id]: prev[selectedPost.id] === reply.id ? null : reply.id }))}
                                      className="text-xs text-gray-500 hover:text-blue-500 transition-colors"
                                    >
                                      답글 달기
                                    </button>
                                    {/* 대댓글 수정/삭제 버튼 (내 댓글인 경우만) */}
                                    {isMyComment(reply) && (
                                      <div className="flex items-center space-x-1">
                                        <button
                                          onClick={() => handleEditComment(selectedPost.id, reply.id, reply.content)}
                                          className="text-gray-400 hover:text-blue-500 transition-colors"
                                          title="댓글 수정"
                                        >
                                          <svg className="w-3 h-3" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M11 5H6a2 2 0 00-2 2v11a2 2 0 002 2h11a2 2 0 002-2v-5m-1.414-9.414a2 2 0 112.828 2.828L11.828 15H9v-2.828l8.586-8.586z" />
                                          </svg>
                                        </button>
                                        <button
                                          onClick={() => handleDeleteComment(reply.id, selectedPost.id)}
                                          className="text-gray-400 hover:text-red-500 transition-colors"
                                          title="댓글 삭제"
                                        >
                                          <svg className="w-3 h-3" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 7l-.867 12.142A2 2 0 0116.138 21H7.862a2 2 0 01-1.995-1.858L5 7m5 4v6m4-6v6m1-10V4a1 1 0 00-1-1h-4a1 1 0 00-1 1v3M4 7h16" />
                                          </svg>
                                        </button>
                                      </div>
                                    )}
                                  </div>
                                </div>
                              </div>
                              
                              {/* 대댓글에 대한 답글 입력 필드 */}
                              {replyingTo[selectedPost.id] === reply.id && (
                                <div className="ml-8 mt-2">
                                  <div className="flex items-center space-x-2">
                                    <div className="w-5 h-5 rounded-full overflow-hidden">
                                      {userInfo?.photo_url ? (
                                        userInfo.photo_url.startsWith('emoji:') ? (
                                          <div className={`w-full h-full bg-gradient-to-br ${anonymousProfile?.avatarColor || 'from-purple-400 to-pink-400'} flex items-center justify-center text-white font-semibold text-xs`}>
                                            {userInfo.photo_url.replace('emoji:', '')}
                                          </div>
                                        ) : (
                                          <img
                                            src={userInfo.photo_url}
                                            alt="프로필"
                                            className="w-full h-full object-cover"
                                          />
                                        )
                                      ) : (
                                        <div className={`w-full h-full bg-gradient-to-br ${anonymousProfile?.avatarColor || 'from-purple-400 to-pink-400'} flex items-center justify-center text-white font-semibold text-xs`}>
                                          {anonymousProfile?.avatarEmoji || (userInfo?.display_name || anonymousProfile?.displayName)?.[0] || 'U'}
                                        </div>
                                      )}
                                    </div>
                                    <input
                                      type="text"
                                      value={newReply[reply.id] || ''}
                                      onChange={(e) => setNewReply(prev => ({ ...prev, [reply.id]: e.target.value }))}
                                      placeholder="답글 추가..."
                                      className="flex-1 text-sm text-gray-900 placeholder-gray-500 border-none focus:outline-none"
                                      onKeyPress={(e) => {
                                        if (e.key === 'Enter') {
                                          submitReply(selectedPost.id, reply.id);
                                        }
                                      }}
                                    />
                                    <button
                                      onClick={() => submitReply(selectedPost.id, reply.id)}
                                      disabled={submittingComment[selectedPost.id] || !newReply[reply.id]?.trim()}
                                      className="text-blue-500 hover:text-blue-600 disabled:text-gray-300 disabled:cursor-not-allowed text-sm font-semibold"
                                    >
                                      게시
                                    </button>
                                  </div>
                                </div>
                              )}
                            </div>
                          ))}
                        </div>
                      )}
                      
                      {/* 답글 입력 필드 */}
                      {replyingTo[selectedPost.id] === comment.id && (
                        <div className="ml-9 mt-2">
                          <div className="flex items-center space-x-2">
                            <div className="w-5 h-5 rounded-full overflow-hidden">
                              {userInfo?.photo_url ? (
                                userInfo.photo_url.startsWith('emoji:') ? (
                                  <div className={`w-full h-full bg-gradient-to-br ${anonymousProfile?.avatarColor || 'from-purple-400 to-pink-400'} flex items-center justify-center text-white font-semibold text-xs`}>
                                    {userInfo.photo_url.replace('emoji:', '')}
                                  </div>
                                ) : (
                                  <img
                                    src={userInfo.photo_url}
                                    alt="프로필"
                                    className="w-full h-full object-cover"
                                  />
                                )
                              ) : (
                                <div className={`w-full h-full bg-gradient-to-br ${anonymousProfile?.avatarColor || 'from-purple-400 to-pink-400'} flex items-center justify-center text-white font-semibold text-xs`}>
                                  {anonymousProfile?.avatarEmoji || (userInfo?.display_name || anonymousProfile?.displayName)?.[0] || 'U'}
                                </div>
                              )}
                            </div>
                            <input
                              type="text"
                              value={newReply[comment.id] || ''}
                              onChange={(e) => setNewReply(prev => ({ ...prev, [comment.id]: e.target.value }))}
                              placeholder="답글 추가..."
                              className="flex-1 text-sm text-gray-900 placeholder-gray-500 border-none focus:outline-none"
                              onKeyPress={(e) => {
                                if (e.key === 'Enter') {
                                  submitReply(selectedPost.id, comment.id);
                                }
                              }}
                            />
                            <button
                              onClick={() => submitReply(selectedPost.id, comment.id)}
                              disabled={submittingComment[selectedPost.id] || !newReply[comment.id]?.trim()}
                              className="text-blue-500 hover:text-blue-600 disabled:text-gray-300 disabled:cursor-not-allowed text-sm font-semibold"
                            >
                              게시
                            </button>
                          </div>
                        </div>
                      )}
                    </div>
                  ))}
                  
                  {loadingComments[selectedPost.id] && (
                    <div className="text-center py-4">
                      <div className="inline-block animate-spin rounded-full h-6 w-6 border-b-2 border-gray-900"></div>
                    </div>
                  )}
                </div>
                
                {/* 댓글 입력 */}
                <div className="p-4 border-t">
                  <div className="flex items-center space-x-3">
                    <div className="w-6 h-6 rounded-full overflow-hidden flex-shrink-0">
                      {userInfo?.photo_url ? (
                        userInfo.photo_url.startsWith('emoji:') ? (
                          <div className={`w-full h-full bg-gradient-to-br ${anonymousProfile?.avatarColor || 'from-purple-400 to-pink-400'} flex items-center justify-center text-white font-semibold text-xs`}>
                            {userInfo.photo_url.replace('emoji:', '')}
                          </div>
                        ) : (
                          <img
                            src={userInfo.photo_url}
                            alt="프로필"
                            className="w-full h-full object-cover"
                          />
                        )
                      ) : (
                        <div className={`w-full h-full bg-gradient-to-br ${anonymousProfile?.avatarColor || 'from-purple-400 to-pink-400'} flex items-center justify-center text-white font-semibold text-xs`}>
                          {anonymousProfile?.avatarEmoji || (userInfo?.display_name || anonymousProfile?.displayName)?.[0] || 'U'}
                        </div>
                      )}
                    </div>
                    <div className="flex-1 flex items-center space-x-2">
                      <input
                        type="text"
                        value={newComment[selectedPost.id] || ''}
                        onChange={(e) => setNewComment(prev => ({ ...prev, [selectedPost.id]: e.target.value }))}
                        placeholder="댓글 추가..."
                        className="flex-1 text-sm text-gray-900 placeholder-gray-500 border-none focus:outline-none"
                        maxLength={500}
                        onKeyPress={(e) => {
                          if (e.key === 'Enter' && !e.shiftKey) {
                            e.preventDefault();
                            submitComment(selectedPost.id);
                          }
                        }}
                      />
                      {newComment[selectedPost.id]?.trim() && (
                        <button
                          onClick={() => submitComment(selectedPost.id)}
                          disabled={submittingComment[selectedPost.id]}
                          className="text-blue-500 font-semibold text-sm hover:text-blue-600 disabled:opacity-50"
                        >
                          {submittingComment[selectedPost.id] ? '등록 중...' : '등록'}
                        </button>
                      )}
                    </div>
                  </div>
                </div>
              </div>
            </div>
          </div>
        </div>
      )}

      {/* 게시글 수정 모달 */}
      {showEditModal && editingPost && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-4">
          <div className="bg-white rounded-xl max-w-2xl w-full max-h-[90vh] overflow-y-auto">
            {/* 헤더 */}
            <div className="flex items-center justify-between p-4 border-b border-gray-200">
              <h2 className="text-lg font-semibold text-gray-900">게시글 수정</h2>
              <button 
                onClick={() => setShowEditModal(false)}
                className="text-gray-400 hover:text-gray-600 transition-colors"
              >
                <svg className="w-6 h-6" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
                </svg>
              </button>
            </div>

            <div className="p-6 space-y-6">
              {/* 현재 이미지 */}
              <div className="space-y-3">
                <label className="block text-sm font-medium text-gray-700">
                  현재 이미지
                </label>
                <div className="relative">
                  <img
                    src={editingPost.image_url}
                    alt="현재 이미지"
                    className="w-full max-h-60 object-cover rounded-lg"
                  />
                </div>
              </div>

              {/* 새 이미지 선택 */}
              <div className="space-y-3">
                <label className="block text-sm font-medium text-gray-700">
                  새 이미지 선택 (선택사항)
                </label>
                <input
                  type="file"
                  accept="image/*"
                  onChange={handleEditImageSelect}
                  className="hidden"
                  id="edit-image-upload"
                />
                
                {editPreviewUrl ? (
                  <div className="relative">
                    <img
                      src={editPreviewUrl}
                      alt="새 이미지 미리보기"
                      className="w-full max-h-60 object-cover rounded-lg"
                    />
                    <button
                      onClick={() => {
                        setEditImage(null);
                        setEditPreviewUrl('');
                        const input = document.getElementById('edit-image-upload') as HTMLInputElement;
                        if (input) input.value = '';
                      }}
                      className="absolute top-2 right-2 w-8 h-8 bg-black bg-opacity-50 text-white rounded-full flex items-center justify-center hover:bg-opacity-70 transition-opacity"
                    >
                      <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
                      </svg>
                    </button>
                  </div>
                ) : (
                  <label
                    htmlFor="edit-image-upload"
                    className="flex flex-col items-center justify-center w-full h-32 border-2 border-dashed border-gray-300 rounded-lg cursor-pointer bg-gray-50 hover:bg-gray-100 transition-colors"
                  >
                    <svg className="w-8 h-8 text-gray-400 mb-2" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M4 16l4.586-4.586a2 2 0 012.828 0L16 16m-2-2l1.586-1.586a2 2 0 012.828 0L20 14m-6-6h.01M6 20h12a2 2 0 002-2V6a2 2 0 00-2-2H6a2 2 0 00-2 2v12a2 2 0 002 2z" />
                    </svg>
                    <p className="text-sm text-gray-600">새 이미지 선택</p>
                  </label>
                )}
              </div>

              {/* 텍스트 수정 */}
              <div className="space-y-2">
                <label className="block text-sm font-medium text-gray-700">
                  내용
                </label>
                <textarea
                  value={editText}
                  onChange={(e) => setEditText(e.target.value)}
                  placeholder="게시글 내용을 수정하세요..."
                  className="w-full p-3 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-transparent resize-none"
                  rows={4}
                  maxLength={500}
                />
                <div className="flex justify-between items-center text-xs text-gray-400">
                  <div></div>
                  <span>{editText.length}/500</span>
                </div>
              </div>
            </div>

            {/* 하단 버튼 */}
            <div className="p-4 border-t border-gray-200 flex space-x-3">
              <button
                onClick={() => setShowEditModal(false)}
                className="flex-1 px-4 py-2 text-gray-700 bg-gray-100 rounded-lg hover:bg-gray-200 transition-colors"
              >
                취소
              </button>
              <button
                onClick={handleEditSubmit}
                disabled={isEditing || !editText.trim()}
                className="flex-1 px-4 py-2 bg-blue-500 text-white rounded-lg hover:bg-blue-600 disabled:bg-gray-300 disabled:cursor-not-allowed transition-colors"
              >
                {isEditing ? '수정 중...' : '수정 완료'}
              </button>
            </div>
          </div>
        </div>
      )}

      {/* 댓글 수정 모달 */}
      {editingComment && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-4">
          <div className="bg-white rounded-xl max-w-md w-full">
            {/* 헤더 */}
            <div className="flex items-center justify-between p-4 border-b border-gray-200">
              <h2 className="text-lg font-semibold text-gray-900">댓글 수정</h2>
              <button 
                onClick={() => setEditingComment(null)}
                className="text-gray-400 hover:text-gray-600 transition-colors"
              >
                <svg className="w-6 h-6" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
                </svg>
              </button>
            </div>

            <div className="p-6 space-y-4">
              {/* 댓글 수정 입력 */}
              <div className="space-y-2">
                <label className="block text-sm font-medium text-gray-700">
                  댓글 내용
                </label>
                <textarea
                  value={editCommentText}
                  onChange={(e) => setEditCommentText(e.target.value)}
                  placeholder="댓글 내용을 수정하세요..."
                  className="w-full p-3 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-transparent resize-none"
                  rows={3}
                  maxLength={500}
                />
                <div className="flex justify-between items-center text-xs text-gray-400">
                  <div></div>
                  <span>{editCommentText.length}/500</span>
                </div>
              </div>
            </div>

            {/* 하단 버튼 */}
            <div className="p-4 border-t border-gray-200 flex space-x-3">
              <button
                onClick={() => setEditingComment(null)}
                className="flex-1 px-4 py-2 text-gray-700 bg-gray-100 rounded-lg hover:bg-gray-200 transition-colors"
              >
                취소
              </button>
              <button
                onClick={handleEditCommentSubmit}
                disabled={!editCommentText.trim()}
                className="flex-1 px-4 py-2 bg-blue-500 text-white rounded-lg hover:bg-blue-600 disabled:bg-gray-300 disabled:cursor-not-allowed transition-colors"
              >
                수정 완료
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}